import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Chart from "react-apexcharts";
import { FaTimes, FaSyncAlt, FaChartLine } from "react-icons/fa";
import { request } from "../../services/api";

const getCurrentThemeKey = () => {
  if (typeof document === "undefined") return "default";
  return document.documentElement.getAttribute("data-sfarm-theme") || "default";
};

const ARCHIVE_THEME_PALETTES = {
  default: {
    isLight: false,
    overlay: "rgba(0, 0, 0, 0.65)",
    modalBg:
      "linear-gradient(145deg, rgba(22, 36, 71, 0.98), rgba(15, 23, 42, 0.98))",
    headerBg: "rgba(15, 23, 42, 0.92)",
    toolbarBg: "rgba(0, 0, 0, 0.08)",
    color: "#fff5e6",
    muted: "rgba(255, 245, 230, 0.68)",
    border: "rgba(255, 245, 230, 0.12)",
    inputBg: "rgba(0, 0, 0, 0.24)",
    inputColor: "#ffffff",
    primary: "#73c2fb",
    primaryColor: "#162447",
    chartText: "#bdc3c7",
    grid: "rgba(255, 255, 255, 0.12)",
    icon: "#f1c40f",
    shadow: "0 28px 90px rgba(0, 0, 0, 0.58)",
  },
  "smart-home": {
    isLight: false,
    overlay: "rgba(0, 0, 0, 0.68)",
    modalBg:
      "linear-gradient(145deg, rgba(12, 23, 48, 0.98), rgba(5, 12, 28, 0.98))",
    headerBg: "rgba(6, 17, 40, 0.92)",
    toolbarBg: "rgba(126, 232, 255, 0.05)",
    color: "#e9fbff",
    muted: "rgba(233, 251, 255, 0.68)",
    border: "rgba(126, 232, 255, 0.18)",
    inputBg: "rgba(0, 0, 0, 0.28)",
    inputColor: "#ffffff",
    primary: "#4de1ff",
    primaryColor: "#061128",
    chartText: "#c9f6ff",
    grid: "rgba(126, 232, 255, 0.14)",
    icon: "#4de1ff",
    shadow: "0 28px 90px rgba(0, 0, 0, 0.6)",
  },
  "industrial-blue": {
    isLight: true,
    overlay: "rgba(18, 38, 58, 0.38)",
    modalBg:
      "linear-gradient(145deg, rgba(229, 243, 251, 0.97), rgba(207, 230, 244, 0.96))",
    headerBg: "rgba(245, 250, 253, 0.58)",
    toolbarBg: "rgba(255, 255, 255, 0.28)",
    color: "#18344f",
    muted: "rgba(24, 52, 79, 0.68)",
    border: "rgba(24, 52, 79, 0.16)",
    inputBg: "rgba(255, 255, 255, 0.86)",
    inputColor: "#18344f",
    primary: "#2f83c7",
    primaryColor: "#ffffff",
    chartText: "#28455f",
    grid: "rgba(24, 52, 79, 0.14)",
    icon: "#2f83c7",
    shadow: "0 28px 90px rgba(24, 52, 79, 0.24)",
  },
  "home-soft": {
    isLight: true,
    overlay: "rgba(31, 45, 58, 0.34)",
    modalBg:
      "linear-gradient(145deg, rgba(255, 255, 255, 0.96), rgba(240, 247, 250, 0.94))",
    headerBg: "rgba(255, 255, 255, 0.58)",
    toolbarBg: "rgba(255, 255, 255, 0.34)",
    color: "#26384c",
    muted: "rgba(38, 56, 76, 0.68)",
    border: "rgba(52, 73, 94, 0.16)",
    inputBg: "rgba(255, 255, 255, 0.9)",
    inputColor: "#26384c",
    primary: "#6a9c89",
    primaryColor: "#ffffff",
    chartText: "#34495e",
    grid: "rgba(52, 73, 94, 0.14)",
    icon: "#6a9c89",
    shadow: "0 28px 90px rgba(31, 45, 58, 0.22)",
  },
};

const useArchiveTheme = (isOpen) => {
  const [themeKey, setThemeKey] = useState(getCurrentThemeKey);

  useEffect(() => {
    if (!isOpen) return undefined;

    const syncTheme = () => setThemeKey(getCurrentThemeKey());
    syncTheme();

    window.addEventListener("sfarm-theme-change", syncTheme);
    window.addEventListener("storage", syncTheme);

    return () => {
      window.removeEventListener("sfarm-theme-change", syncTheme);
      window.removeEventListener("storage", syncTheme);
    };
  }, [isOpen]);

  return ARCHIVE_THEME_PALETTES[themeKey] || ARCHIVE_THEME_PALETTES.default;
};

const buildArchiveStyles = (theme) => ({
  overlay: {
    position: "fixed",
    inset: 0,
    background: theme.overlay,
    zIndex: 99999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    backdropFilter: "blur(6px)",
  },
  modal: {
    width: "min(1000px, 95vw)",
    maxHeight: "90vh",
    background: theme.modalBg,
    color: theme.color,
    borderRadius: "18px",
    boxShadow: theme.shadow,
    overflow: "hidden",
    border: `1px solid ${theme.border}`,
  },
  header: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 48px",
    borderBottom: `1px solid ${theme.border}`,
    background: theme.headerBg,
    direction: "rtl",
    textAlign: "center",
  },
  iconButton: {
    position: "absolute",
    left: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    border: `1px solid ${theme.border}`,
    background: theme.isLight
      ? "rgba(255,255,255,0.72)"
      : "rgba(255,255,255,0.06)",
    color: theme.color,
    cursor: "pointer",
    fontSize: "16px",
    width: "34px",
    height: "34px",
    borderRadius: "11px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "18px",
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.border}`,
    background: theme.toolbarBg,
    direction: "rtl",
  },
  toolbarItem: {
    flex: "1 1 0",
    display: "flex",
  },
  filterItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    direction: "rtl",
    color: theme.color,
  },
  select: {
    background: theme.inputBg,
    color: theme.inputColor,
    border: `1px solid ${theme.border}`,
    borderRadius: "8px",
    padding: "6px 8px",
    outline: "none",
  },
  refreshButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: theme.primary,
    color: theme.primaryColor,
    border: "none",
    borderRadius: "8px",
    padding: "7px 12px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: 800,
  },
  info: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "18px",
    padding: "10px 16px",
    fontSize: "12px",
    color: theme.muted,
    direction: "rtl",
    textAlign: "right",
    borderBottom: `1px solid ${theme.border}`,
  },
  infoItem: {
    flex: "1 1 0",
  },
  error: {
    margin: "10px 16px 0",
    padding: "10px",
    background: "rgba(231, 76, 60, 0.15)",
    border: "1px solid rgba(231, 76, 60, 0.4)",
    color: theme.isLight ? "#9f2d22" : "#ffb3b3",
    borderRadius: "8px",
    fontSize: "13px",
  },
  chartWrapper: {
    padding: "16px",
    direction: "ltr",
  },
});

const ArchiveChartModal = ({
  isOpen,
  onClose,
  serial,
  sensorKey,
  title,
  unit,
}) => {
  const [range, setRange] = useState("-24h");
  const [limit, setLimit] = useState(1000);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const archiveTheme = useArchiveTheme(isOpen);
  const archiveStyles = useMemo(
    () => buildArchiveStyles(archiveTheme),
    [archiveTheme],
  );

  const displaySensorName = title || sensorKey;

  const loadArchive = async () => {
    if (!serial || !sensorKey) {
      setError(
        `اطلاعات سنسور کامل نیست. serial=${serial || "خالی"} sensor=${sensorKey || "خالی"}`,
      );
      setRecords([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const json = await request(
        `/get_sensor_archive.php?serial=${encodeURIComponent(serial)}&sensor=${encodeURIComponent(sensorKey)}&range=${encodeURIComponent(range)}&limit=${encodeURIComponent(limit)}`,
      );

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در دریافت داده نمودار");
      }

      const sorted = [...(json.records || [])]
        .filter(
          (row) => row.time && row.value !== null && row.value !== undefined,
        )
        .sort(
          (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
        );

      setRecords(sorted);
    } catch (err) {
      console.error("Archive chart error:", err);
      setError(err.message || "خطای نامشخص");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadArchive();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, serial, sensorKey, range, limit]);

  if (!isOpen) return null;

  const chartData = records
    .map((row) => ({
      x: new Date(row.time).getTime(),
      y: Number(row.value),
    }))
    .filter((point) => !Number.isNaN(point.x) && !Number.isNaN(point.y));

  const series = [
    {
      name: displaySensorName,
      data: chartData,
    },
  ];

  const isLongRange = ["-7d", "-30d", "-90d"].includes(range);

  const formatPersianDateTime = (timestamp) => {
    try {
      return new Intl.DateTimeFormat("fa-IR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(timestamp));
    } catch {
      return "";
    }
  };

  const formatPersianTime = (timestamp) => {
    try {
      return new Intl.DateTimeFormat("fa-IR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(timestamp));
    } catch {
      return "";
    }
  };

  const formatPersianAxisLabel = (timestamp) => {
    if (isLongRange) {
      return formatPersianDateTime(timestamp);
    }

    return formatPersianTime(timestamp);
  };

  const options = {
    chart: {
      type: "line",
      height: 420,
      background: "transparent",
      foreColor: archiveTheme.chartText,
      toolbar: {
        show: true,
      },
      zoom: {
        enabled: true,
        type: "x",
        autoScaleYaxis: true,
      },
      animations: {
        enabled: false,
      },
    },
    stroke: {
      curve: "stepline",
      width: 2,
    },
    markers: {
      size: 0,
      strokeWidth: 0,
      hover: {
        size: 3,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
        rotate: -35,
        trim: true,
        style: {
          colors: archiveTheme.chartText,
          fontSize: "11px",
        },
        formatter: function (value, timestamp) {
          return formatPersianAxisLabel(timestamp || value);
        },
      },
    },
    yaxis: {
      title: {
        text: unit ? `مقدار (${unit})` : "مقدار",
        style: {
          color: archiveTheme.chartText,
        },
      },
      labels: {
        style: {
          colors: archiveTheme.chartText,
        },
      },
    },
    tooltip: {
      enabled: true,
      theme: archiveTheme.isLight ? "light" : "dark",
      style: {
        fontSize: "12px",
        fontFamily: "IRANSans, Arial, sans-serif",
      },
      x: {
        formatter: function (value) {
          return formatPersianDateTime(value);
        },
      },
      y: {
        formatter: (val) => `${val}${unit ? ` ${unit}` : ""}`,
      },
    },
    grid: {
      borderColor: archiveTheme.grid,
    },
    noData: {
      text: loading ? "در حال دریافت..." : "داده‌ای برای نمایش وجود ندارد",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: archiveTheme.chartText,
        fontSize: "14px",
      },
    },
  };

  return createPortal(
    <div style={archiveStyles.overlay}>
      <div style={archiveStyles.modal}>
        <div style={archiveStyles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaChartLine color={archiveTheme.icon} />
            <strong>نمودار آرشیو {displaySensorName}</strong>
          </div>

          <button onClick={onClose} style={archiveStyles.iconButton}>
            <FaTimes />
          </button>
        </div>

        <div style={archiveStyles.toolbar}>
          <div
            style={{
              ...archiveStyles.toolbarItem,
              justifyContent: "flex-start",
            }}
          >
            <div style={archiveStyles.filterItem}>
              <span>بازه:</span>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                style={archiveStyles.select}
              >
                <option value="-1h">۱ ساعت اخیر</option>
                <option value="-6h">۶ ساعت اخیر</option>
                <option value="-12h">۱۲ ساعت اخیر</option>
                <option value="-24h">۲۴ ساعت اخیر</option>
                <option value="-7d">۷ روز اخیر</option>
                <option value="-30d">۳۰ روز اخیر</option>
                <option value="-90d">۹۰ روز اخیر</option>
              </select>
            </div>
          </div>

          <div
            style={{ ...archiveStyles.toolbarItem, justifyContent: "center" }}
          >
            <div style={archiveStyles.filterItem}>
              <span>تعداد:</span>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                style={archiveStyles.select}
              >
                <option value={200}>۲۰۰</option>
                <option value={500}>۵۰۰</option>
                <option value={1000}>۱۰۰۰</option>
                <option value={2000}>۲۰۰۰</option>
                <option value={5000}>۵۰۰۰</option>
              </select>
            </div>
          </div>

          <div
            style={{ ...archiveStyles.toolbarItem, justifyContent: "flex-end" }}
          >
            <button
              onClick={loadArchive}
              style={archiveStyles.refreshButton}
              disabled={loading}
            >
              <FaSyncAlt />
              {loading ? "در حال دریافت..." : "بروزرسانی"}
            </button>
          </div>
        </div>

        <div style={archiveStyles.info}>
          <span style={{ ...archiveStyles.infoItem, textAlign: "right" }}>
            دستگاه: {serial}
          </span>
          <span style={{ ...archiveStyles.infoItem, textAlign: "center" }}>
            سنسور: {displaySensorName}
          </span>
          <span style={{ ...archiveStyles.infoItem, textAlign: "left" }}>
            تعداد رکورد: {records.length}
          </span>
        </div>

        {error && <div style={archiveStyles.error}>{error}</div>}

        <div style={archiveStyles.chartWrapper}>
          <Chart options={options} series={series} type="line" height={420} />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ArchiveChartModal;
